import { useMemo, useRef, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { HelpCircle, Loader2, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useGuida } from "@/hooks/useKb";

/**
 * Pulsante "?" + drawer con il manuale del modulo (KB). Riusabile su ogni modulo:
 * basta `<GuidaModulo modulo="M2" />` nell'header. Indice a 2 livelli, ricerca,
 * link interni (#slug) e markdown.
 */
export function GuidaModulo({ modulo }: { modulo: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1 text-sm font-medium text-muted-foreground hover:bg-accent/40 hover:text-foreground"
          title="Guida del modulo"
        >
          <HelpCircle className="size-4" />
          Guida
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-[480px] flex-col border-l bg-card shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right"
        >
          {open && <GuidaContenuto modulo={modulo} onClose={() => setOpen(false)} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function GuidaContenuto({ modulo, onClose }: { modulo: string; onClose: () => void }) {
  const { data, isLoading } = useGuida(modulo, true);
  const [q, setQ] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const sezioni = useMemo(() => data?.sezioni ?? [], [data]);
  const filtrate = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return sezioni;
    return sezioni.filter(
      (s) => s.titolo.toLowerCase().includes(t) || s.contenuto_md.toLowerCase().includes(t),
    );
  }, [sezioni, q]);

  // indice a 2 livelli: livello 1 con figlie (parent_slug)
  const indice = useMemo(() => {
    const top = filtrate.filter((s) => s.livello === 1).sort((a, b) => a.ordine - b.ordine);
    return top.map((s) => ({
      sezione: s,
      figlie: filtrate
        .filter((c) => c.livello === 2 && c.parent_slug === s.slug)
        .sort((a, b) => a.ordine - b.ordine),
    }));
  }, [filtrate]);

  function vaiA(slug: string) {
    const el = scrollRef.current?.querySelector(`[data-slug="${slug}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <>
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <HelpCircle className="size-5 text-primary" />
        <Dialog.Title className="text-base font-semibold">
          {data?.guida.titolo ?? "Guida"}
        </Dialog.Title>
        <Dialog.Description className="sr-only">
          {data?.guida.descrizione ?? "Manuale del modulo"}
        </Dialog.Description>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
          aria-label="Chiudi"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="border-b px-4 py-2.5">
        <div className="flex items-center gap-2 rounded-md border bg-muted px-2.5 py-1.5">
          <Search className="size-3.5 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca nella guida…"
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Caricamento…
          </div>
        ) : !data ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Guida non ancora disponibile per questo modulo.
          </p>
        ) : (
          <>
            {/* indice */}
            {!q && (
              <nav className="mb-5 rounded-lg border bg-muted/40 p-3 text-sm">
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Indice
                </div>
                <ul className="space-y-0.5">
                  {indice.map(({ sezione, figlie }) => (
                    <li key={sezione.slug}>
                      <button
                        type="button"
                        onClick={() => vaiA(sezione.slug)}
                        className="text-left hover:text-primary hover:underline"
                      >
                        {sezione.titolo}
                      </button>
                      {figlie.length > 0 && (
                        <ul className="ml-3 mt-0.5 space-y-0.5 border-l pl-2">
                          {figlie.map((f) => (
                            <li key={f.slug}>
                              <button
                                type="button"
                                onClick={() => vaiA(f.slug)}
                                className="text-left text-[13px] text-muted-foreground hover:text-primary hover:underline"
                              >
                                {f.titolo}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            {/* sezioni */}
            <div className="space-y-6">
              {filtrate
                .slice()
                .sort((a, b) => a.ordine - b.ordine)
                .map((s) => (
                  <section key={s.slug} data-slug={s.slug} className="scroll-mt-3">
                    <div className="mb-1.5 flex items-center gap-2">
                      <h3
                        className={cn(
                          "font-semibold",
                          s.livello === 2 ? "text-[13.5px] text-foreground/90" : "text-[15px]",
                        )}
                      >
                        {s.titolo}
                      </h3>
                      {s.audience !== "utente" && (
                        <span className="rounded bg-secondary px-1.5 py-px text-[10px] text-muted-foreground">
                          {s.audience === "dominio" ? "concetti" : "tecnico"}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 text-[13.5px] leading-relaxed text-muted-foreground">
                      <Markdown text={s.contenuto_md} onAnchor={vaiA} />
                    </div>
                  </section>
                ))}
              {filtrate.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Nessun risultato per «{q}».
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Renderer markdown minimale (zero-dep): bold, code, liste, link interni ──

function Markdown({ text, onAnchor }: { text: string; onAnchor: (slug: string) => void }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let lista: string[] = [];
  let key = 0;

  const flush = () => {
    if (lista.length) {
      blocks.push(
        <ul key={`ul-${key++}`} className="list-disc space-y-1 pl-5">
          {lista.map((l, i) => (
            <li key={i}>{renderInline(l, onAnchor)}</li>
          ))}
        </ul>,
      );
      lista = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("- ")) {
      lista.push(line.slice(2));
    } else if (!line.trim()) {
      flush();
    } else {
      flush();
      blocks.push(
        <p key={`p-${key++}`} className="text-foreground/80">
          {renderInline(line, onAnchor)}
        </p>,
      );
    }
  }
  flush();
  return <>{blocks}</>;
}

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string, onAnchor: (slug: string) => void): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      out.push(
        <strong key={key++} className="font-semibold text-foreground">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("`")) {
      out.push(
        <code key={key++} className="rounded bg-muted px-1 py-0.5 font-mono text-[12px]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      const mm = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      if (mm) {
        const [, label, href] = mm;
        if (href.startsWith("#")) {
          out.push(
            <button
              key={key++}
              type="button"
              onClick={() => onAnchor(href.slice(1))}
              className="text-primary underline-offset-2 hover:underline"
            >
              {label}
            </button>,
          );
        } else {
          out.push(
            <a
              key={key++}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline-offset-2 hover:underline"
            >
              {label}
            </a>,
          );
        }
      } else {
        out.push(tok);
      }
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
